import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { CommentActionType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData as LegacyCommentData,
	CommentsDataMap as LegacyCommentsDataMap,
	DataMap as LegacyDataMap,
	ReplyData as LegacyReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData as NewCommentData,
	CommentsDataMap as NewCommentsDataMap,
	ReplyData as NewReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import type { CommentLocation } from '@confluence/comments-panel-queries/entry-points/CommentLocation.ts';
import type {
	CommentInlineLocation,
	ActiveCommentInlineResolveProperties,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import type { CommentInlineResolveProperties } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';

import { getCommentsSortedOldestToNewest } from './commentsPanelHelper.ts';
import { ViewValues } from './useCommentsPanel.ts';

type CommentData = LegacyCommentData | NewCommentData;
type ReplyData = LegacyReplyData | NewReplyData;
type CommentOrReplyData = CommentData | ReplyData;
type ReplyDataOrNull = ReplyData | null | undefined;

type CommentWithRemovalStatus = Pick<CommentOrReplyData, 'wasRemovedByAnotherUser'>;
type IsCommentValid = (comment: CommentOrReplyData) => boolean;
type VisibleInlineCommentsByDanglingStatus = [CommentData[], CommentData[]];

type GetVisibleCommentThreadsOptions = {
	currentView?: ViewValues;
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	includeInlineComments: boolean;
	includeGeneralComments: boolean;
	orderedAnnotationIds: string[];
	isNewDanglingCommentExperienceEnabled?: boolean;
	isCommentValid?: IsCommentValid;
	pageMode: PageMode;
};

const isParentCommentOpen = (commentData: CommentData): boolean => commentData.isOpen;

const wasCommentRemovedByAnotherUser = (comment: CommentWithRemovalStatus): boolean =>
	Boolean(comment.wasRemovedByAnotherUser);

const wasCommentDeletedByAnotherUser = (comment: CommentWithRemovalStatus): boolean =>
	comment.wasRemovedByAnotherUser === CommentActionType.DELETE_COMMENT;

const isInlineLocation = (
	location: CommentLocation | CommentData['location'],
): location is CommentInlineLocation => {
	return location && 'inlineMarkerRef' in location;
};

const isParentComment = (comment: CommentOrReplyData): comment is CommentData =>
	'isOpen' in comment;

type GetVisibleGeneralCommentsOptions = {
	isCommentValid: IsCommentValid;
	comments: Record<string, CommentData>;
};

const getVisibleGeneralComments = ({
	isCommentValid,
	comments,
}: GetVisibleGeneralCommentsOptions): CommentData[] =>
	Object.values(comments).reduce<CommentData[]>((acc, comment) => {
		if (isCommentValid(comment)) {
			acc.push({
				...comment,
				replies: (comment.replies as ReplyData[]).filter(
					(reply: ReplyDataOrNull) => reply && isCommentValid(reply),
				) as ReplyData[],
			} as CommentData);
		}
		return acc;
	}, []);

const getNextInlineResolveProperties = (
	inlineResolveProperties:
		| ActiveCommentInlineResolveProperties
		| CommentInlineResolveProperties
		| null
		| undefined,
	isDangling: boolean,
): { inlineResolveProperties?: ActiveCommentInlineResolveProperties } => {
	if (!inlineResolveProperties) return {};

	return {
		inlineResolveProperties: {
			...inlineResolveProperties,
			isDangling,
		},
	};
};

const updateCommentLocation = (
	comment: CommentData,
	isDangling: boolean,
): CommentData['location'] => {
	const commentLocation = comment.location;
	if (!isInlineLocation(commentLocation)) return commentLocation;

	// When the experiment is enabled and the resolved-properties spread would
	// produce a structurally identical object (i.e. isDangling is unchanged
	// and there are no inlineResolveProperties to merge), short-circuit by
	// returning the existing location reference. This stops every render of
	// CommentsPanelList from minting a new `location` object — which today
	// cascades into a new `comment` object via getNextComment, breaking
	// React.memo on the row components.
	if (expValEquals('confluence_comments_panel_memo_rows', 'isEnabled', true)) {
		const existingResolveProperties = commentLocation.inlineResolveProperties;
		if (!existingResolveProperties) {
			// Nothing to merge in; the spread would produce an equivalent object.
			return commentLocation;
		}
		if (existingResolveProperties.isDangling === isDangling) {
			// All fields would be identical; preserve reference.
			return commentLocation;
		}
	}

	return {
		...commentLocation,
		...getNextInlineResolveProperties(commentLocation.inlineResolveProperties, isDangling),
	} as CommentData['location'];
};

const getNextComment = (comment: CommentData, isDangling: boolean): CommentData => {
	// When the experiment is enabled, only allocate a new wrapper if the
	// derived `location` actually changed. updateCommentLocation now returns
	// the original location reference when it would be structurally identical,
	// so we can safely short-circuit here on referential equality.
	if (expValEquals('confluence_comments_panel_memo_rows', 'isEnabled', true)) {
		const nextLocation = updateCommentLocation(comment, isDangling);
		if (nextLocation === comment.location) {
			return comment;
		}
		return {
			...comment,
			location: nextLocation,
		} as CommentData;
	}

	const { replies, ...rest } = comment;

	return {
		...rest,
		replies,
		location: updateCommentLocation(comment, isDangling),
	} as CommentData;
};

const getVisibleInlineComments = (
	isCommentValid: IsCommentValid,
	comments: LegacyDataMap | Record<string, NewCommentData>,
	orderedAnnotationIds: string[],
	_isNewDanglingCommentExperienceEnabled: boolean,
): VisibleInlineCommentsByDanglingStatus => {
	const activeMarkerRefs = new Set(orderedAnnotationIds);
	const nonDangling: CommentData[] = [];
	const dangling: CommentData[] = [];

	const validComments = Object.values(comments).reduce<CommentData[]>((acc, comment) => {
		if (isCommentValid(comment)) {
			if (expValEquals('confluence_comments_panel_memo_rows', 'isEnabled', true)) {
				// Only build a new replies array if filtering would actually drop
				// any entries; otherwise reuse the original array reference. Then
				// only build a new comment wrapper if the replies reference
				// changed. This keeps the input reference whenever the validity
				// status of the comment and its replies is unchanged across
				// renders, allowing React.memo on row components to bail out.
				const filteredReplies = comment.replies.filter(isCommentValid);
				const replies =
					filteredReplies.length === comment.replies.length ? comment.replies : filteredReplies;
				if (replies === comment.replies) {
					acc.push(comment);
				} else {
					acc.push({
						...comment,
						replies,
					});
				}
			} else {
				acc.push({
					...comment,
					replies: comment.replies.filter(isCommentValid),
				});
			}
		}
		return acc;
	}, []);

	for (const comment of validComments) {
		const commentLocation = comment.location;
		const inlineMarkerRef = isInlineLocation(commentLocation)
			? commentLocation.inlineMarkerRef
			: null;

		const isDangling = !inlineMarkerRef || !activeMarkerRefs.has(inlineMarkerRef);
		const updatedComment = getNextComment(comment, isDangling);

		if (isDangling) {
			dangling.push(updatedComment);
		} else {
			nonDangling.push(updatedComment);
		}
	}

	return [nonDangling, dangling];
};

export const isCommentResolved = (comment: CommentOrReplyData): boolean => {
	if (wasCommentDeletedByAnotherUser(comment)) return false;

	return isParentComment(comment) ? !isParentCommentOpen(comment) : true;
};

export const isCommentOpen = (comment: CommentOrReplyData): boolean => {
	if (wasCommentRemovedByAnotherUser(comment)) return false;

	return isParentComment(comment) ? isParentCommentOpen(comment) : true;
};

const sortInlineCommentsByOrderedActiveAnnotationIdList = (
	comments: CommentData[],
	orderedAnnotationIds: string[],
): CommentData[] => {
	const commentsByMarkerRef = new Map(
		comments.map((comment) => {
			const commentLocation = comment.location;
			const inlineMarkerRef = isInlineLocation(commentLocation)
				? commentLocation.inlineMarkerRef
				: null;

			return [inlineMarkerRef, comment];
		}),
	);

	return orderedAnnotationIds
		.map((id) => commentsByMarkerRef.get(id))
		.filter((comment: CommentData | undefined): comment is CommentData => comment !== undefined);
};

const getVisibleInlineThreadsForView = (
	currentView: ViewValues,
	sortedNonDanglingComments: (LegacyCommentData | NewCommentData)[],
	danglingComments: (LegacyCommentData | NewCommentData)[],
) => {
	switch (currentView) {
		case ViewValues.OPEN:
			return sortedNonDanglingComments;
		case ViewValues.DELETED_CONTENT:
			return danglingComments;
		default:
			return [...sortedNonDanglingComments, ...danglingComments];
	}
};

type GetVisibleInlineThreadsOptions = {
	isCommentValid: IsCommentValid;
	commentsDataMap: LegacyCommentsDataMap | NewCommentsDataMap;
	orderedAnnotationIds: string[];
	currentView: ViewValues;
	isNewDanglingCommentExperienceEnabled: boolean;
};

const getVisibleInlineThreads = ({
	isCommentValid,
	commentsDataMap,
	orderedAnnotationIds,
	currentView,
	isNewDanglingCommentExperienceEnabled,
}: GetVisibleInlineThreadsOptions): CommentData[] => {
	const [nonDanglingComments, danglingComments] = getVisibleInlineComments(
		isCommentValid,
		commentsDataMap.inline,
		orderedAnnotationIds,
		isNewDanglingCommentExperienceEnabled,
	);

	const sortedNonDanglingComments = sortInlineCommentsByOrderedActiveAnnotationIdList(
		nonDanglingComments,
		orderedAnnotationIds,
	);

	const visibleInlineThreadsForView = isNewDanglingCommentExperienceEnabled
		? getVisibleInlineThreadsForView(currentView, sortedNonDanglingComments, danglingComments)
		: [...sortedNonDanglingComments, ...danglingComments];

	return fg('confluence_frontend_content_wrapper')
		? visibleInlineThreadsForView
		: sortedNonDanglingComments;
};

type GetIsCommentValidOptions = {
	currentView: ViewValues;
};

const getIsCommentValid = ({ currentView }: GetIsCommentValidOptions): IsCommentValid => {
	switch (currentView) {
		case ViewValues.RESOLVED:
			return isCommentResolved;
		default:
			return isCommentOpen;
	}
};

// Determine if general comments are enabled based on the user's selection and - if using the new
// Comments Data Manager -  the user is not in Edit mode. In the legacy commments data management,
// general comments were not loaded into the commentsDataMap when the page is in Edit mode and thus
// not added to the visible threads list. But in the Comments Data Manager, all comments are loaded
// regardless of the page mode, and since we still don't want to have general comments in the list
// in Edit mode we need to explicitly hide them.
const isGeneralCommentsEnabled = ({
	includeGeneralComments,
	pageMode,
}: {
	includeGeneralComments: boolean;
	pageMode: PageMode;
}): boolean =>
	expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? includeGeneralComments && pageMode !== PageMode.EDIT
		: includeGeneralComments;

export const getVisibleCommentThreads = ({
	commentsDataMap,
	orderedAnnotationIds,
	includeInlineComments,
	includeGeneralComments,
	currentView = ViewValues.OPEN,
	isNewDanglingCommentExperienceEnabled = fg('confluence_frontend_new_dangling_comments_ux'),
	isCommentValid: providedIsCommentValid,
	pageMode,
}: GetVisibleCommentThreadsOptions): CommentData[] => {
	const threads: CommentData[] = [];

	const isCommentValid =
		providedIsCommentValid ??
		getIsCommentValid({
			currentView,
		});

	if (includeInlineComments) {
		const inlineThreads = getVisibleInlineThreads({
			isCommentValid,
			commentsDataMap,
			orderedAnnotationIds,
			currentView,
			isNewDanglingCommentExperienceEnabled,
		});
		threads.push(...inlineThreads);
	}

	if (isGeneralCommentsEnabled({ includeGeneralComments, pageMode })) {
		const generalThreads = getVisibleGeneralComments({
			isCommentValid,
			comments: commentsDataMap.general,
		}).sort(getCommentsSortedOldestToNewest);
		threads.push(...generalThreads);
	}

	return threads;
};
